import type { Pool } from "./data";

export function parseMarketCapRankLimit(marketCapFilter: string): number | null {
	if (marketCapFilter === "top_50") return 50;
	if (marketCapFilter === "top_100") return 100;
	if (marketCapFilter === "top_200") return 200;
	return null;
}

export function passesMarketCapRankFilter(pool: Pool, marketCapFilter: string): boolean {
	const rankLimit = parseMarketCapRankLimit(marketCapFilter);
	if (rankLimit == null) return true;

	const token1Rank = pool.displayToken1MarketCapRank ?? null;
	const token2Rank = pool.displayToken2MarketCapRank ?? null;
	const token1IsAltcoin = pool.token1Category === "Altcoin";
	const token2IsAltcoin = pool.token2Category === "Altcoin";

	if (token1IsAltcoin !== token2IsAltcoin) {
		const altcoinRank = token1IsAltcoin ? token1Rank : token2Rank;
		return altcoinRank != null && altcoinRank <= rankLimit;
	}

	return (
		(token1Rank != null && token1Rank <= rankLimit) ||
		(token2Rank != null && token2Rank <= rankLimit)
	);
}

export interface MarketCapQualifyingToken {
	side: "token1" | "token2";
	symbol: string;
	rank: number;
	coinGeckoId?: string;
	category: Pool["token1Category"];
}

/** Tokens whose rank satisfied the market-cap filter for this pool. */
export function getMarketCapFilterQualifyingTokens(
	pool: Pool,
	marketCapFilter: string,
): MarketCapQualifyingToken[] {
	const rankLimit = parseMarketCapRankLimit(marketCapFilter);
	if (rankLimit == null || !passesMarketCapRankFilter(pool, marketCapFilter)) {
		return [];
	}

	const sides: Array<{
		side: "token1" | "token2";
		symbol: string;
		rank: number | null | undefined;
		coinGeckoId?: string;
		category: Pool["token1Category"];
	}> = [
		{
			side: "token1",
			symbol: pool.token1,
			rank: pool.displayToken1MarketCapRank,
			coinGeckoId: pool.coinGeckoDisplayToken1Id,
			category: pool.token1Category,
		},
		{
			side: "token2",
			symbol: pool.token2,
			rank: pool.displayToken2MarketCapRank,
			coinGeckoId: pool.coinGeckoDisplayToken2Id,
			category: pool.token2Category,
		},
	];

	const token1IsAltcoin = pool.token1Category === "Altcoin";
	const token2IsAltcoin = pool.token2Category === "Altcoin";

	if (token1IsAltcoin !== token2IsAltcoin) {
		const altcoinSide = token1IsAltcoin ? sides[0] : sides[1];
		if (altcoinSide.rank != null && altcoinSide.rank <= rankLimit) {
			return [
				{
					side: altcoinSide.side,
					symbol: altcoinSide.symbol,
					rank: altcoinSide.rank,
					coinGeckoId: altcoinSide.coinGeckoId,
					category: altcoinSide.category,
				},
			];
		}
		return [];
	}

	return sides
		.filter(
			(side): side is typeof side & { rank: number } => side.rank != null && side.rank <= rankLimit,
		)
		.map((side) => ({
			side: side.side,
			symbol: side.symbol,
			rank: side.rank,
			coinGeckoId: side.coinGeckoId,
			category: side.category,
		}));
}
